import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, TrendingUp, MessageSquareText, BookOpen, PenLine, Check, Clock, Loader2, GraduationCap, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ColorLegend } from "@/components/ColorLegend";
import { NotifyFamilyButton } from "@/components/EmailNotify";
import { EvalChart, LevelDisplay, EvalLegend } from "@/lib/evalSystem";
import { TeacherShell } from "@/components/TeacherShell";

const shortWeek = (wn) => "S" + (wn?.split("-W")[1] ?? "");

function formatRange(startISO, endISO) {
  try {
    const f = (d, o) => new Intl.DateTimeFormat("fr-FR", o).format(new Date(d));
    return `${f(startISO, { day: "numeric", month: "short" })} – ${f(endISO, {
      day: "numeric", month: "short", year: "numeric",
    })}`;
  } catch { return wnFallback(); }
}
function wnFallback() { return "—"; }

function formatSignedAt(iso) {
  try {
    const d = new Date(iso);
    const day = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Paris" }).format(d);
    const parts = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Paris" }).formatToParts(d);
    const h = parts.find((p) => p.type === "hour")?.value ?? "00";
    const m = parts.find((p) => p.type === "minute")?.value ?? "00";
    return `${day} à ${h} h ${m}`;
  } catch { return ""; }
}

function ColorDots({ p, system, criteria }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {(criteria || []).map((c) => (
        <span key={c.id} className="flex items-center gap-1.5" title={c.label}>
          <LevelDisplay system={system} levelId={p.values?.[c.id]} />
          <span className="hidden text-xs text-slate-500 sm:inline">{c.label.split(" ")[0]}</span>
        </span>
      ))}
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <Icon size={16} className="text-slate-400" /> {title}
      </h2>
      {children}
    </section>
  );
}

export default function StudentProfile() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/students/${studentId}/profile`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Accès refusé." : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const passports = useMemo(() => data?.passports || [], [data]);
  const recent = useMemo(() => [...passports].reverse(), [passports]); // plus récent d'abord

  const chartData = useMemo(
    () => passports.map((p) => ({ week: shortWeek(p.week_number), ...(p.levels || {}) })),
    [passports]
  );

  const lastComments = recent.filter((p) => p.commentaire?.trim()).slice(0, 5);
  const lastNotions = recent.filter((p) => p.notions_a_revoir?.trim()).slice(0, 5);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] dark:bg-[#0A0A0A]">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }
  if (!data) return null;
  const s = data.student;

  return (
    <TeacherShell active="dashboard" contentClassName="w-full">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        {/* SP-HEADER */}
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform duration-200 hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="back-button"
            aria-label="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Fiche élève</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              data-testid="print-profile-button"
            >
              <Printer size={16} /> <span className="hidden sm:inline">Imprimer / PDF</span>
            </button>
            <NotifyFamilyButton
              classId={s.class_id}
              studentId={s.id}
              studentName={`${s.first_name} ${s.last_name}`.trim()}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="flex flex-col gap-5"
        >
          {/* Identité */}
          <div className="flex items-center gap-4" data-testid="student-identity">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white dark:bg-white dark:text-slate-900">
              <GraduationCap size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                {s.first_name} {s.last_name}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {s.class_name}{s.class_level ? ` · ${s.class_level}` : ""}
              </p>
            </div>
          </div>

          {/* Graphique d'évolution */}
          <Section icon={TrendingUp} title="Évolution" testid="evolution-section">
            {chartData.length < 2 ? (
              <p className="py-8 text-center text-sm text-slate-400">Pas assez de semaines pour tracer une évolution.</p>
            ) : (
              <div data-testid="evolution-chart">
                <EvalChart data={chartData} criteria={data.criteria} scale={data.chart_scale} height={256} />
              </div>
            )}
          </Section>

          {/* Derniers Passeports */}
          <Section icon={BookOpen} title="Derniers Passeports" testid="recent-passports-section">
            <div className="flex flex-col gap-2.5">
              {recent.slice(0, 4).map((p) => (
                <div key={p.week_number} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2.5 dark:border-white/10" data-testid={`recent-passport-${p.week_number}`}>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{formatRange(p.week_start, p.week_end)}</span>
                    <ColorDots p={p} system={p.eval_system || data.eval_system} criteria={p.eval_system?.criteria || data.criteria} />
                  </div>
                </div>
              ))}
            </div>
            {data.eval_system?.type === "passeport" ? (
              <ColorLegend showIntro className="mt-4 border-t border-slate-100 pt-4 dark:border-white/10" />
            ) : (
              <EvalLegend system={data.eval_system} className="mt-4 border-t border-slate-100 pt-4 dark:border-white/10" />
            )}
          </Section>

          {/* Derniers commentaires */}
          <Section icon={MessageSquareText} title="Derniers commentaires" testid="comments-section">
            {lastComments.length ? (
              <ul className="flex flex-col gap-3">
                {lastComments.map((p) => (
                  <li key={p.week_number} className="text-sm">
                    <span className="text-xs text-slate-400">{shortWeek(p.week_number)} · {formatRange(p.week_start, p.week_end)}</span>
                    <p className="text-slate-700 dark:text-slate-200">{p.commentaire}</p>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-400">Aucun commentaire pour le moment.</p>}
          </Section>

          {/* Dernières notions à revoir */}
          <Section icon={BookOpen} title="Dernières notions à revoir" testid="notions-section">
            {lastNotions.length ? (
              <ul className="flex flex-col gap-3">
                {lastNotions.map((p) => (
                  <li key={p.week_number} className="text-sm">
                    <span className="text-xs text-slate-400">{shortWeek(p.week_number)} · {formatRange(p.week_start, p.week_end)}</span>
                    <p className="text-slate-700 dark:text-slate-200">{p.notions_a_revoir}</p>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-400">Aucune notion à revoir enregistrée.</p>}
          </Section>

          {/* Historique des signatures */}
          <Section icon={PenLine} title="Historique des signatures" testid="signatures-section">
            {data.signatures.length ? (
              <ul className="flex flex-col gap-2.5">
                {data.signatures.map((sig) => (
                  <li key={`${sig.week_number}-${sig.signed_at}`} className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2.5 text-sm dark:bg-emerald-500/10" data-testid={`signature-item-${sig.week_number}`}>
                    <span className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
                      <Check size={15} /> {sig.parent_name}
                    </span>
                    <span className="text-xs text-emerald-700/80 dark:text-emerald-400/80">
                      {shortWeek(sig.week_number)} · {formatSignedAt(sig.signed_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-sm text-slate-400">
                <Clock size={15} /> Aucune signature pour le moment.
              </p>
            )}
          </Section>
        </motion.div>
      </main>
    </TeacherShell>
  );
}
